import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { LoadingButton, Notice, PageHeader } from '@routes-plan/ui';
import { setPageTitle } from '../../utils/pageTitle';
import { ScreenTourButton } from '../../components/help/ScreenHelp';
import { useT, type TextKey } from '../../i18n';

/**
 * Preferencias de avisos de la persona: por cada tipo de aviso, si llega a la campana, al correo y al
 * celular. Cada persona cambia solo lo suyo (no requiere permiso de menú).
 */
interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

type Channel = 'web' | 'email' | 'push';
const CHANNELS: Channel[] = ['web', 'email', 'push'];

interface Pref {
  category: string;
  channels: Channel[];
  web: boolean;
  email: boolean;
  push: boolean;
}

const NotificationPreferences: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const t = useT();
  setPageTitle('notifications.pref.title');
  const [prefs, setPrefs] = useState<Pref[]>([]);
  const [saved, setSaved] = useState<Pref[]>([]);
  const [hasEmail, setHasEmail] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const dirty = useMemo(() => JSON.stringify(prefs) !== JSON.stringify(saved), [prefs, saved]);

  const apply = (data: any) => {
    const list: Pref[] = Array.isArray(data?.data) ? data.data : [];
    setPrefs(list);
    setSaved(list);
    setHasEmail(data?.hasEmail !== false);
    setLoaded(true);
  };

  useEffect(() => {
    (async () => {
      showLoading();
      try {
        const { data } = await axios.get('notifications/preferences');
        apply(data);
      } catch {
        showAlert(t('common.loadError'), 'error');
      } finally {
        hideLoading();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (category: string, channel: Channel, value: boolean) =>
    setPrefs((prev) => prev.map((p) => (p.category === category ? { ...p, [channel]: value } : p)));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put('notifications/preferences', {
        preferences: prefs.map(({ category, web, email, push }) => ({ category, web, email, push })),
      });
      apply(data);
      showAlert(t('notifications.pref.saved'), 'success');
    } catch {
      showAlert(t('common.saveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ minWidth: 0, maxWidth: 1040, mx: 'auto', width: '100%' }}>
      <PageHeader
        title={t('notifications.pref.title')}
        description={t('notifications.pref.intro')}
        helpAction={<ScreenTourButton />}
        actions={
          <LoadingButton variant="contained" onClick={save} loading={saving} disabled={!loaded || !dirty} data-tour="notification-prefs-save">
            {t('notifications.pref.save')}
          </LoadingButton>
        }
      />
      <Stack spacing={2}>
        {loaded && !hasEmail && (
          <Box data-tour="notification-prefs-no-email" sx={{ minWidth: 0 }}>
            <Notice severity="info">{t('notifications.pref.noEmail')}</Notice>
          </Box>
        )}
        {prefs.length > 0 && (
          <Paper variant="outlined" sx={{ overflow: 'hidden' }} data-tour="notification-prefs-list">
            {prefs.map((p, index) => (
            <Box
              key={p.category}
              sx={{
                display: 'grid',
                gap: { xs: 1.5, md: 3 },
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) auto' },
                alignItems: 'center',
                px: { xs: 2, sm: 2.5 },
                py: 2,
                borderTop: index ? 1 : 0,
                borderColor: 'divider',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {t(`notifications.pref.category.${p.category}` as TextKey)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t(`notifications.pref.category.${p.category}Help` as TextKey)}
                </Typography>
              </Box>
              <Box
                role="group"
                data-tour={index === 0 ? 'notification-prefs-channels' : undefined}
                aria-label={t(`notifications.pref.category.${p.category}` as TextKey)}
                sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', md: 'repeat(3, 132px)' }, gap: 1 }}
              >
                {CHANNELS.map((c) => {
                  const available = p.channels.includes(c) && (c !== 'email' || hasEmail);
                  return (
                    <FormControlLabel
                      key={c}
                      sx={{ m: 0, minWidth: 0, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}
                      control={<Switch checked={available && p[c]} disabled={!available} onChange={(e) => toggle(p.category, c, e.target.checked)} />}
                      label={
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2">{t(`notifications.pref.channel.${c}` as TextKey)}</Typography>
                          {!p.channels.includes(c) && (
                            <Typography variant="caption" color="text.secondary">
                              {t('notifications.pref.notAvailable')}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  );
                })}
              </Box>
            </Box>
            ))}
          </Paper>
        )}
        {loaded && (
          <Typography variant="caption" color="text.secondary" data-tour="notification-prefs-push">
            {t('notifications.pref.pushHelp')}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default NotificationPreferences;
